import {
  COHORT_FIELD_DEFINITIONS,
  FIELD_DEFINITIONS,
  type RuleArea,
  type RuleSummary,
  SAMPLE_RULE_FIELD_DEFINITIONS,
} from "@unbox/api";

type FieldDefinitions = Record<string, { kind: string; label: string }>;

const FIELD_DEFINITIONS_BY_AREA: Record<RuleArea, FieldDefinitions> = {
  cohort: COHORT_FIELD_DEFINITIONS,
  sample: SAMPLE_RULE_FIELD_DEFINITIONS,
  blocklist: FIELD_DEFINITIONS,
};

const CURRENCY_FIELDS = new Set(["gmvAmount", "gmvLast30Days"]);
const MAX_VISIBLE_CONDITIONS = 2;

const OPERATOR_SYMBOLS: Record<string, string> = {
  eq: "=",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
};

type ConditionGroup = RuleSummary["conditions"]["when"][number];
type Condition = ConditionGroup["conditions"][number];

const compactNumber = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

const formatValue = (field: string, value: number, currency: string) =>
  CURRENCY_FIELDS.has(field)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value)
    : compactNumber.format(value);

const describeCondition = (definitions: FieldDefinitions, condition: Condition, currency: string): string | null => {
  const definition = definitions[condition.field];
  if (!definition) return null;

  if (definition.kind === "tag") {
    return condition.operator === "is_not" ? `Not in ${definition.label}` : `In ${definition.label}`;
  }

  if (condition.operator === "between") {
    if (!Array.isArray(condition.value) || condition.value.length !== 2) return null;
    const [min, max] = condition.value;
    if (typeof min !== "number" || typeof max !== "number") return null;
    return `${definition.label} ${formatValue(condition.field, min, currency)}–${formatValue(condition.field, max, currency)}`;
  }

  const symbol = OPERATOR_SYMBOLS[condition.operator];
  if (!symbol || typeof condition.value !== "number") return null;
  return `${definition.label} ${symbol} ${formatValue(condition.field, condition.value, currency)}`;
};

const describeGroups = (
  definitions: FieldDefinitions,
  groups: readonly ConditionGroup[],
  currency: string,
): { parts: string[]; joiner: string } => {
  const joiner = groups.length === 1 && groups[0].logic === "any" ? " or " : " + ";
  const parts = groups
    .flatMap((group): readonly Condition[] => group.conditions)
    .map((condition) => describeCondition(definitions, condition, currency))
    .filter((part): part is string => part !== null);
  return { parts, joiner };
};

export const describeRuleLogic = ({ area, conditions }: RuleSummary, currency: string): string => {
  const definitions = FIELD_DEFINITIONS_BY_AREA[area];
  const when = describeGroups(definitions, conditions.when, currency);
  if (when.parts.length === 0) return "";

  const visible = when.parts.slice(0, MAX_VISIBLE_CONDITIONS).join(when.joiner);
  const hidden = when.parts.length - MAX_VISIBLE_CONDITIONS;
  const text = hidden > 0 ? `${visible} +${hidden} more` : visible;

  const except = describeGroups(definitions, conditions.except, currency);
  if (except.parts.length === 0) return text;

  return `${text}, except ${except.parts.slice(0, MAX_VISIBLE_CONDITIONS).join(except.joiner)}`;
};
